/**
 * DraftTab — iter87.174. Persistent admin entry point for the draft
 * workflow. Lives inside the AdminPanel Bento grid so admins never
 * lose the "Start Auto-Draft" CTA after dismissing the dashboard
 * StartDraftBanner.
 *
 * States (mutually exclusive):
 *   - `golf_position_info`: golf_position_draft_api template — the
 *     auto-draft happens server-side inside the golf engine when
 *     Round 1 opens. Admin has no button to press; tile just
 *     explains what will happen.
 *   - `locked`: first game has already kicked off. Buttons grayed,
 *     copy reflects "Draft locked — first game has kicked off." For
 *     golf position draft this fires on `golf.r1_started`.
 *   - `already_drafted_pre_ko`: a draft doc exists in
 *     `league.drafts_by_tournament[tid]` AND no game has kicked off
 *     yet. Start button hidden — Reset lives in Danger Zone.
 *   - `open`: no draft doc yet, no game kicked off. Show
 *     Start Auto-Draft CTA that routes to the Draft Room.
 *
 * Reset is intentionally NOT part of this tile. It lives on the
 * Danger Zone tile (iter87.6+) so destructive actions stay
 * consolidated. Same for Lock crew picks.
 *
 * The tile itself is only mounted for tournaments that use an
 * admin-triggered auto-draft OR the golf position-draft server-side
 * auto-draft. See `AdminPanel.jsx::hasDraftAdminSurface` for the
 * full gating.
 */
import { Link } from "react-router-dom";
import { Zap, Lock, Info, CheckCircle2 } from "lucide-react";

export default function DraftTab({ leagueId, tournament, league }) {
    const tid = tournament?.id;
    const isGolfPositionDraft = tournament?.template === "golf_position_draft_api";
    const hasDraftHere = !!(tid && league?.drafts_by_tournament?.[tid]);
    const seasonStarted = !!tournament?.round_started?.group
        || Object.values(tournament?.round_started || {}).some(Boolean);
    const golfR1Started = !!tournament?.golf?.r1_started;
    const playStarted = isGolfPositionDraft ? golfR1Started : seasonStarted;

    // Order matters: play-started beats already-drafted beats open.
    let state = "open";
    if (playStarted) state = "locked";
    else if (isGolfPositionDraft) state = "golf_position_info";
    else if (hasDraftHere) state = "already_drafted_pre_ko";

    return (
        <div className="space-y-4 min-w-0" data-testid="draft-tab">
            <div className="flex items-center gap-3 min-w-0">
                <h2 className="font-display font-extrabold text-xl uppercase break-words">Draft</h2>
            </div>
            {state === "open" && (
                <div
                    className="brutal-card p-5 space-y-4 border-volt/40"
                    data-testid="draft-state-open"
                >
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                            <Zap className="w-4 h-4 text-black" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0 space-y-1">
                            <p className="font-display font-extrabold text-base uppercase leading-tight">Kick off the draft</p>
                            <p className="text-xs text-zinc-400 leading-relaxed">
                                Nothing scores until crewmates get their teams. Head to the Draft Room, set teams per crewmate, and fire the auto-draft.
                            </p>
                        </div>
                    </div>
                    <Link
                        to={`/league/${leagueId}/draft`}
                        data-testid="draft-tile-start-btn"
                        className="brutal-btn text-sm py-3 bg-volt text-black border-black shadow-brutal inline-flex items-center gap-2 w-full justify-center"
                    >
                        <Zap className="w-4 h-4" strokeWidth={2.5} /> Start Auto-Draft
                    </Link>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Need to reset a draft you already ran? Head to <span className="text-blaze font-bold">Danger Zone → Clear this tournament's draft</span>.
                    </p>
                </div>
            )}
            {state === "already_drafted_pre_ko" && (
                <div
                    className="brutal-card p-5 space-y-3 border-cyber/40"
                    data-testid="draft-state-drafted-pre-ko"
                >
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-cyber/15 border border-cyber/40 flex items-center justify-center shrink-0 rounded-md">
                            <CheckCircle2 className="w-4 h-4 text-cyber" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0 space-y-1">
                            <p className="font-display font-extrabold text-base uppercase leading-tight">Draft complete</p>
                            <p className="text-xs text-zinc-400 leading-relaxed">
                                Teams are assigned. The draft locks the moment the first game kicks off. If you need to re-run it before then, use <span className="text-blaze font-bold">Danger Zone → Clear this tournament's draft</span>.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {state === "golf_position_info" && (
                <div
                    className="brutal-card p-5 space-y-3 border-cyber/40"
                    data-testid="draft-state-golf-position-info"
                >
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-cyber/15 border border-cyber/40 flex items-center justify-center shrink-0 rounded-md">
                            <Info className="w-4 h-4 text-cyber" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0 space-y-2">
                            <p className="font-display font-extrabold text-base uppercase leading-tight">Auto-draft runs at tee-off</p>
                            <p className="text-xs text-zinc-400 leading-relaxed">
                                Position Draft golf assigns each crewmate their finish positions automatically when Round 1 tees off. Nothing to press — just make sure the crew is set before start time.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {state === "locked" && (
                <div
                    className="brutal-card p-5 space-y-3 opacity-70"
                    data-testid="draft-state-locked"
                >
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 rounded-md">
                            <Lock className="w-4 h-4 text-zinc-400" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0 space-y-1">
                            <p className="font-display font-extrabold text-base uppercase leading-tight text-zinc-300">Draft locked</p>
                            <p className="text-xs text-zinc-500 leading-relaxed">
                                First game has kicked off — the draft can no longer be run or reset. Rosters are frozen for the rest of the tournament.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        disabled
                        data-testid="draft-tile-locked-btn"
                        className="brutal-btn text-sm py-3 bg-white/[0.04] border-white/10 text-zinc-500 inline-flex items-center gap-2 w-full justify-center cursor-not-allowed"
                    >
                        <Lock className="w-4 h-4" strokeWidth={2.5} /> Draft locked
                    </button>
                </div>
            )}
        </div>
    );
}
